import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CaretDown, SignOut, UserCircle, ShieldCheck, Palette } from '@phosphor-icons/react';

import type { PlatformAdmin } from '@/features/platform-auth/store';

/**
 * The console's account area, top right.
 *
 * It replaces an identity label with a bare sign-out icon beside it. That arrangement had two
 * problems: the operator's own account had NO route to it anywhere in the header, and the only
 * thing the header offered was the single destructive action — one stray click from being signed
 * out of the console that governs every tenant, with no menu to land in first.
 *
 * So: identity, the way to the account, and sign-out — in that order, behind one deliberate
 * click. Mirrors the chambers header (`components/layout/TopBar.tsx`) in structure and in
 * dismissal behaviour; the colours come from `--plat-*` because this shell's palette is its own.
 *
 * Sign-out reuses the shell's existing handler. There is no second authentication path here.
 */

const ITEMS = [
  { to: '/platform/settings/account',    label: 'My Account', Icon: UserCircle },
  { to: '/platform/settings/security',   label: 'Security',   Icon: ShieldCheck },
  { to: '/platform/settings/appearance', label: 'Appearance', Icon: Palette },
] as const;

export function PlatformAccountMenu({
  admin,
  onSignOut,
}: {
  admin: PlatformAdmin | null;
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  // Dismissal: a click anywhere else, or Escape. A menu that can only be closed by picking
  // something from it is a trap, and this one sits next to Sign out.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const name = admin ? `${admin.firstName} ${admin.lastName}` : '';

  return (
    <div ref={wrap} className="relative">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors duration-200
          hover:bg-plat-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-plat-focus"
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-plat-info-soft text-xs font-bold text-plat-info-text">
          {admin?.firstName?.[0]}{admin?.lastName?.[0]}
        </span>
        <span className="hidden text-left sm:block">
          <span className="block text-sm font-medium leading-tight text-plat-fg">{name}</span>
          <span className="block text-[11px] leading-tight text-plat-muted">Platform Admin</span>
        </span>
        <CaretDown className="h-3.5 w-3.5 text-plat-muted" />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Account"
          className="absolute right-0 top-11 z-20 w-64 overflow-hidden rounded-xl border border-plat-border bg-plat-elevated py-1.5 shadow-lg"
        >
          <div className="border-b border-plat-border px-4 py-3">
            <div className="text-sm font-semibold text-plat-fg">{name}</div>
            <div className="truncate text-xs text-plat-muted">{admin?.email}</div>
          </div>

          <div className="py-1">
            {ITEMS.map(({ to, label, Icon }) => (
              <Link
                key={to}
                to={to}
                role="menuitem"
                onClick={() => setOpen(false)}
                className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-sm text-plat-secondary transition-colors hover:bg-plat-hover hover:text-plat-fg"
              >
                <Icon className="h-4 w-4 text-plat-muted" />
                {label}
              </Link>
            ))}
          </div>

          <div className="border-t border-plat-border pt-1">
            <button
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); onSignOut(); }}
              className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-sm text-plat-danger-text transition-colors hover:bg-plat-danger-soft"
            >
              <SignOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
